/**
 * Color palettes with per-palette slot mapping
 *
 * Palettes define colors as an array, then map semantic slot names
 * to indices. Each palette can assign its colors to slots differently.
 */

/** Semantic slot names */
export const PALETTE_SLOTS = [
  'background',
  'foreground',
  'primary',
  'secondary',
  'accent',
  'highlight',
  'shadow'
] as const;

export type PaletteSlot = typeof PALETTE_SLOTS[number];

/** Mapping from slot names to color indices */
export type SlotMapping = Record<PaletteSlot, number>;

export interface ColorPalette {
  id: string;
  name: string;
  colors: string[];       // Array of hex colors
  mapping: SlotMapping;   // Per-palette: slot -> color index
}

/** Get color for a semantic slot from a palette */
export function getSlotColor(palette: ColorPalette, slot: PaletteSlot): string {
  const index = palette.mapping[slot];
  return palette.colors[index] ?? '#888888';
}

/**
 * Default palettes
 */
export const defaultPalettes: ColorPalette[] = [
  {
    id: 'midcentury',
    name: 'Mid-Century',
    colors: [
      '#232020',   // 0: charcoal
      '#dab98b',   // 1: cream/sand
      '#c69a6b',   // 2: tan/camel
      '#657456',   // 3: olive green
      '#c65d33',   // 4: burnt orange
      '#b87333',   // 5: copper
      '#405d5d'    // 6: teal
    ],
    mapping: {
      background: 0,
      foreground: 1,
      primary: 2,
      secondary: 3,
      accent: 4,
      highlight: 5,
      shadow: 6
    }
  },
  {
    id: 'riley',
    name: 'Riley',
    colors: [
      '#19234b',   // 0: dark navy
      '#fadcb4',   // 1: cream white
      '#415aa5',   // 2: royal blue
      '#648cc8',   // 3: medium blue
      '#c86488',   // 4: pink/magenta
      '#f0b450',   // 5: gold
      '#328c64'    // 6: emerald
    ],
    mapping: {
      background: 0,
      foreground: 1,
      primary: 2,
      secondary: 3,
      accent: 4,
      highlight: 5,
      shadow: 6
    }
  },
  {
    id: 'monochrome',
    name: 'Monochrome',
    colors: [
      '#1a1a1a',   // 0: near black
      '#f0f0f0',   // 1: near white
      '#888888',   // 2: mid grey
      '#666666',   // 3: dark grey
      '#ffffff',   // 4: white
      '#cccccc',   // 5: light grey
      '#333333'    // 6: charcoal
    ],
    mapping: {
      background: 0,
      foreground: 1,
      primary: 2,
      secondary: 3,
      accent: 4,
      highlight: 5,
      shadow: 6
    }
  }
];
